import api from '@/services/base/BaseApi';
import {
  TRqBatchPayment,
  TBatchPaymentResponse,
  TScheduledBankPaymentGroupState,
  TScheduledBankPaymentGroupType,
  TBatchPaymentPollStatus,
  BatchPaymentPollStatusEnum,
  ScheduledBankPaymentGroupStateEnum
} from '@/features/payments/types/BatchPayment';
import { handleApiError } from '@/services/base/handleApiError';
import {
  BATCH_LIMIT,
  PaymentRowKindEnum,
  TBatchOverviewPayment,
  TPaymentListItem,
  TScheduledPaymentDetail
} from '@/features/payments/manage/types/ManagePayments';

// Backend response DTO for a 2xx result. COP/payee mismatches no longer arrive here -
// they're now surfaced as a 400 COP_VALIDATION_FAILED error (see useBatchPayment.ts).
interface IRsScheduledBankPaymentEmergeGroup {
  id?: string;
  name: string;
  firstPaymentDate?: string;
  walletId: string;
  totalAmount?: number;
  state?: TScheduledBankPaymentGroupState;
  totalPayments?: number;
  completedPayments?: number;
  failedPayments?: number;
  multiSignActionId?: string;
}

// Transform backend response to frontend format
const transformBatchPaymentResponse = (
  backendResponse: IRsScheduledBankPaymentEmergeGroup
): TBatchPaymentResponse => {
  const state = backendResponse.state || ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL;
  const failedPayments = backendResponse.failedPayments || 0;

  // Determine status and message based on backend state and payment counts
  let status: TBatchPaymentPollStatus = BatchPaymentPollStatusEnum.PENDING;
  let message = 'Your batch payment has been scheduled';

  if (state === ScheduledBankPaymentGroupStateEnum.APPROVED) {
    status = BatchPaymentPollStatusEnum.SCHEDULED;
  } else if (state === ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL) {
    status = BatchPaymentPollStatusEnum.PENDING;
    message = 'Your batch payment is waiting for approval';
  } else if (state === ScheduledBankPaymentGroupStateEnum.IN_PROGRESS) {
    status = BatchPaymentPollStatusEnum.IN_PROGRESS;
    message = 'Your batch payment is being processed...';
  } else if (state === ScheduledBankPaymentGroupStateEnum.COMPLETED) {
    status = BatchPaymentPollStatusEnum.COMPLETED;
    message = 'Your batch payment has completed';
  } else if (state === ScheduledBankPaymentGroupStateEnum.COMPLETED_WITH_FAILURES) {
    status = BatchPaymentPollStatusEnum.COMPLETED_WITH_FAILURES;
    message = `Some payments couldn't be processed (${
      backendResponse.completedPayments || 0
    } of ${backendResponse.totalPayments || 0} succeeded)`;
  } else if (
    state === ScheduledBankPaymentGroupStateEnum.FAILED ||
    state === ScheduledBankPaymentGroupStateEnum.REJECTED
  ) {
    status = BatchPaymentPollStatusEnum.FAILED;
    message = "Your batch payment couldn't be processed. Please try again.";
  } else if (state === ScheduledBankPaymentGroupStateEnum.EXPIRED) {
    status = BatchPaymentPollStatusEnum.EXPIRED;
    message = 'This batch payment request has expired. Please create a new one.';
  }

  // Safety net: if every payment in the batch has failed, treat this as a failure
  // regardless of what `state` says. `state` is the only signal the mapping above
  // relies on - if it's ever missing, unrecognized, or out of sync with the counts
  // (seen in practice when the bank rejects the whole batch), status would otherwise
  // silently stay at its PENDING default and the UI would never leave the
  // in-progress screen even though the batch has clearly finished failing.
  const totalPayments = backendResponse.totalPayments || 0;
  const completedPayments = backendResponse.completedPayments || 0;
  const allPaymentsFailed =
    totalPayments > 0 && completedPayments === 0 && failedPayments === totalPayments;

  if (allPaymentsFailed && status !== BatchPaymentPollStatusEnum.FAILED) {
    status = BatchPaymentPollStatusEnum.FAILED;
    message = "Your batch payment couldn't be processed. Please try again.";
  }

  return {
    status,
    id: backendResponse.id,
    batchId: backendResponse.id,
    name: backendResponse.name,
    message,
    firstPaymentDate: backendResponse.firstPaymentDate,
    walletId: backendResponse.walletId,
    totalAmount: backendResponse.totalAmount,
    state,
    totalPayments: backendResponse.totalPayments,
    completedPayments: backendResponse.completedPayments,
    failedPayments,
    multiSignActionId: backendResponse.multiSignActionId
  };
};

export const sendBatchPayment = async (
  batchData: TRqBatchPayment
): Promise<TBatchPaymentResponse> => {
  const responseBody = await api.post<IRsScheduledBankPaymentEmergeGroup, TRqBatchPayment>(
    'api/v1/bank/scheduled-payment-groups',
    batchData
  );
  return transformBatchPaymentResponse(responseBody.data);
};

export const getScheduledPaymentGroupStatus = async (
  groupId: string
): Promise<TBatchPaymentResponse> => {
  const responseBody = await api.get<IRsScheduledBankPaymentEmergeGroup>(
    `api/v1/bank/scheduled-payment-groups/${groupId}`
  );
  return transformBatchPaymentResponse(responseBody.data);
};

export interface TGetScheduledPaymentGroupsParams {
  accountId: string;
  types: TScheduledBankPaymentGroupType[];
  states: TScheduledBankPaymentGroupState[];
}

interface IRsScheduledBankPaymentGroup {
  id: string;
  name: string;
  firstPaymentDate: string;
  lastSyncDate: string;
  walletId: string;
  totalAmount: number;
  state: TScheduledBankPaymentGroupState;
  type: TScheduledBankPaymentGroupType;
  totalPayments: number;
  completedPayments?: number;
  failedPayments?: number;
  forceCompleted?: boolean;
  /** Only populated for `emerge` type groups; xero groups don't have this yet. */
  createdByCustomerId?: string;
  /** Set once someone has approved the group; absent while still awaiting approval. */
  approverCustomerId?: string;
  /** Id of the multi-sign action gating this group's approval; used to authorise/reject it. */
  multiSignActionId?: string;
}

const getScheduledPaymentGroupsRequestParams = (options: TGetScheduledPaymentGroupsParams) => {
  const urlParams = new URLSearchParams();
  urlParams.append('accountId', options.accountId);
  options.types.forEach((type) => urlParams.append('types', type));
  options.states.forEach((state) => urlParams.append('states', state));
  return urlParams.toString();
};

// Transform backend response to frontend format
const transformScheduledPaymentGroup = (group: IRsScheduledBankPaymentGroup): TPaymentListItem => ({
  id: group.id,
  name: group.name,
  kind: PaymentRowKindEnum.BATCH,
  type: group.type,
  createdDate: group.lastSyncDate,
  scheduledDate: group.firstPaymentDate,
  totalAmount: group.totalAmount,
  walletId: group.walletId,
  state: group.state,
  totalPayments: group.totalPayments,
  failedPayments: group.failedPayments,
  completedPayments: group.completedPayments,
  forceCompleted: group.forceCompleted,
  createdByCustomerId: group.createdByCustomerId,
  approverCustomerId: group.approverCustomerId,
  multiSignActionId: group.multiSignActionId
});

export const getScheduledPaymentGroups = async (
  options: TGetScheduledPaymentGroupsParams
): Promise<TPaymentListItem[]> => {
  const responseBody = await api
    .get<
      IRsScheduledBankPaymentGroup[]
    >(`api/v1/bank/scheduled-payment-groups?${getScheduledPaymentGroupsRequestParams(options)}`)
    .catch(handleApiError);
  return responseBody.data.map(transformScheduledPaymentGroup);
};

interface IRsScheduledBankPayment {
  id: string;
  amount: number;
  paymentDate: string;
  payeeName: string;
  payeeBankAccount: string;
  payeeParticulars?: string | null;
  payeeAnalysis?: string | null;
  payeeReference?: string | null;
  payerParticulars?: string | null;
  payerAnalysis?: string | null;
  payerReference?: string | null;
  lastPaymentState?: 'completed' | 'failed';
}

interface IRsScheduledBankPaymentGroupPaymentsListPageable {
  payments: IRsScheduledBankPayment[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
}

const transformScheduledPaymentDetail = (
  payment: IRsScheduledBankPayment
): TScheduledPaymentDetail => ({
  id: payment.id,
  payeeName: payment.payeeName,
  payeeBankAccount: payment.payeeBankAccount,
  payeeParticulars: payment.payeeParticulars,
  payeeAnalysis: payment.payeeAnalysis,
  payeeReference: payment.payeeReference,
  payerParticulars: payment.payerParticulars,
  payerAnalysis: payment.payerAnalysis,
  payerReference: payment.payerReference
});

/**
 * Fetches the first payment of a scheduled payment group (page 0, size 1). Used to surface
 * per-payment details (payee account, statement details) in the scheduled payment modal.
 */
export const getScheduledPaymentGroupFirstPayment = async (
  groupId: string
): Promise<TScheduledPaymentDetail | null> => {
  const params = new URLSearchParams({ page: '0', size: '1' });
  const responseBody = await api
    .get<IRsScheduledBankPaymentGroupPaymentsListPageable>(
      `api/v1/bank/scheduled-payment-groups/${groupId}/payments?${params.toString()}`
    )
    .catch(handleApiError);
  const [firstPayment] = responseBody.data.payments;
  return firstPayment ? transformScheduledPaymentDetail(firstPayment) : null;
};

const transformBatchOverviewPayment = (
  payment: IRsScheduledBankPayment
): TBatchOverviewPayment => ({
  id: payment.id,
  payeeName: payment.payeeName,
  payeeBankAccount: payment.payeeBankAccount,
  amount: payment.amount,
  payeeParticulars: payment.payeeParticulars,
  payeeAnalysis: payment.payeeAnalysis,
  payeeReference: payment.payeeReference,
  paymentDate: payment.paymentDate,
  lastPaymentState: payment.lastPaymentState
});

/**
 * Fetches every payment in a scheduled payment group for the Batch Overview table. Capped at
 * BATCH_LIMIT - see that constant for why.
 */
export const getScheduledPaymentGroupPayments = async (
  groupId: string
): Promise<TBatchOverviewPayment[]> => {
  const params = new URLSearchParams({
    page: '0',
    size: String(BATCH_LIMIT)
  });
  const responseBody = await api
    .get<IRsScheduledBankPaymentGroupPaymentsListPageable>(
      `api/v1/bank/scheduled-payment-groups/${groupId}/payments?${params.toString()}`
    )
    .catch(handleApiError);
  return responseBody.data.payments.map(transformBatchOverviewPayment);
};

interface IRsScheduledBankPaymentGroupActionResponse {
  state: TScheduledBankPaymentGroupState;
  forceCompleted?: boolean;
}

/** Retries a failed/partially-failed batch. Response reflects the group's new state. */
export const retryScheduledPaymentGroup = (groupId: string) =>
  api
    .post<
      IRsScheduledBankPaymentGroupActionResponse,
      null
    >(`api/v1/bank/scheduled-payment-groups/${groupId}/retry`, null)
    .catch(handleApiError);

/** Force-completes a failed/partially-failed batch. Response reflects the group's new state. */
export const markScheduledPaymentGroupAsComplete = (groupId: string) =>
  api
    .patch<
      IRsScheduledBankPaymentGroupActionResponse,
      null
    >(`api/v1/bank/scheduled-payment-groups/${groupId}/force-complete`, null)
    .catch(handleApiError);

/**
 * Deletes an EMERGE batch while it's still APPROVED/PENDING_APPROVAL and not yet actioned - see
 * DeleteScheduledBankPaymentGroupUseCase on the backend. Can 400/409 with
 * BatchDeletionErrorCodeEnum (see useScheduledPaymentAction) if the batch's state changed - e.g.
 * force-completed, or a payment in it is due today/overdue and now within the processing window -
 * since the delete dialog opened.
 */
export const deleteScheduledPaymentGroup = (groupId: string) =>
  api
    .delete<unknown, undefined>(`api/v1/bank/scheduled-payment-groups/${groupId}`)
    .catch(handleApiError);
